/**
 * AtlasNetworkModule — SpacetimeDB connection, world resolution, AOI, and the
 * table→state bridge.
 *
 * Owns the authoritative link. It:
 *   - connects and routes identity resolution to the IdentityModule,
 *   - subscribes to the small public `account`/`world` tables to resolve the
 *     live Commons id and the player's personal world id,
 *   - manages AOI subscriptions for the high-cardinality `player_state` and
 *     `world_object` tables with Manhattan-distance hysteresis (subscribe-new-
 *     then-unsubscribe-old to avoid cache gaps),
 *   - bridges row callbacks into the shared `atlas` state + `bus` (remote
 *     avatars are data; meshes are rendered by the avatar/world modules).
 *
 * The client never trusts itself: it sends movement *intent* via the `move`
 * reducer and renders only what the authoritative tables report.
 */

import type { App, IModule } from 'enginex';
import { connect } from '../net/connection.js';
import { markResume, pendingResume, clearResume, reconnectDelayMs, serverPingUrl, RECONNECT_GIVE_UP_MS } from '../lib/session_resume.js';
import { STDB_WS_URI } from '../config.js';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { commonsSpawnPoint } from '../lib/ports.js';
import { makeSystem, Phase } from '../engine-compat.js';
import { worldToCell, cellManhattan, cellRange, AOI_HYSTERESIS_CELLS } from '../config.js';
import { nearbyPlayersSql, worldObjectsSql, versonaStateSql, versonaRouteSql } from '../net/queries.js';
import type { AtlasIdentityModule } from './AtlasIdentityModule.js';
import type { DbConnection } from '../module_bindings/index.js';
import type { SubscriptionHandle } from '../module_bindings/index.js';
import type { AvatarAppearance } from '@shared/avatar.js';
import type { Asset, InventoryItem, AssetKind } from '@shared/types/inventory.js';
import type {
  PulseState,
  CompassState,
  OrbitState,
  OrbitMemberState,
  NeedSatisfier,
  NeedName,
} from '@shared/types/social.js';
import type {
  ReputationScores,
  VouchEdge,
  Review,
  BetrayalRecord,
} from '@shared/types/reputation.js';
import type {
  EventSpec,
  EventAttendance,
  RitualState,
  RitualMute,
  NpcHost,
  MemoryEntry,
  MemoryEntryKind,
} from '@shared/types/events.js';
import type {
  Group,
  GroupMember,
  GroupSignature,
  CharterChallenge,
  TreasurySpend,
  SpendVote,
  ForkVote,
  ForkBallot,
  GroupState,
  GroupRole,
} from '@shared/types/groups.js';

/** Structural shape of an `avatar` row (avoids importing the generated row type). */
interface AvatarRowLike {
  identity: { toHexString(): string };
  bodyTemplate: string;
  skeletonId: string;
  materialSlots: string;
  attachments: string;
  complexity: number;
}

/** Structural shape of an `asset` row. */
interface AssetRowLike {
  assetId: bigint;
  creator: { toHexString(): string };
  kind: string;
  contentRef: string;
  permCopy: boolean;
  permModify: boolean;
  permTransfer: boolean;
  permRemix: boolean;
  permTemplate: boolean;
  isPremium: boolean;
  parentAssetId: bigint;
  createdAt: { microsSinceUnixEpoch: bigint };
}

/** Structural shape of an `inventory_item` row. */
interface InventoryRowLike {
  itemId: bigint;
  owner: { toHexString(): string };
  assetId: bigint;
  nextOwnerCopy: boolean;
  nextOwnerModify: boolean;
  nextOwnerTransfer: boolean;
  acquiredAt: { microsSinceUnixEpoch: bigint };
}

/** Structural shape of a `wallet` row. */
interface WalletRowLike {
  identity: { toHexString(): string };
  sov: bigint;
  inf: bigint;
}

/** Structural shape of a `market_listing` row. */
interface MarketListingRowLike {
  listingId: bigint;
  seller: { toHexString(): string };
  assetId: bigint;
  priceSov: bigint;
  active: boolean;
  listedAt: { microsSinceUnixEpoch: bigint };
}

function assetRowToShared(row: AssetRowLike): Asset {
  return {
    assetId: row.assetId,
    creator: row.creator.toHexString(),
    kind: row.kind as AssetKind,
    contentRef: row.contentRef,
    permCopy: row.permCopy,
    permModify: row.permModify,
    permTransfer: row.permTransfer,
    permRemix: row.permRemix,
    permTemplate: row.permTemplate,
    isPremium: row.isPremium,
    parentAssetId: row.parentAssetId,
    createdAt: row.createdAt.microsSinceUnixEpoch,
  };
}

function inventoryRowToShared(row: InventoryRowLike): InventoryItem {
  return {
    itemId: row.itemId,
    owner: row.owner.toHexString(),
    assetId: row.assetId,
    nextOwnerCopy: row.nextOwnerCopy,
    nextOwnerModify: row.nextOwnerModify,
    nextOwnerTransfer: row.nextOwnerTransfer,
    acquiredAt: row.acquiredAt.microsSinceUnixEpoch,
  };
}

/** Structural shape of a `pulse` row (P6 Social Pulse). */
interface PulseRowLike {
  identity: { toHexString(): string };
  social: number;
  fun: number;
  creativity: number;
  rest: number;
  visSocial: boolean;
  visFun: boolean;
  visCreativity: boolean;
  visRest: boolean;
  updatedAt: { microsSinceUnixEpoch: bigint };
}

/** Structural shape of a `compass` row (P6 Social Compass). */
interface CompassRowLike {
  identity: { toHexString(): string };
  want: string;
  fear: string;
  rotatedAt: { microsSinceUnixEpoch: bigint };
  public: boolean;
}

/** Structural shape of an `orbit` row (P6 Orbits). */
interface OrbitRowLike {
  orbitId: bigint;
  createdAt: { microsSinceUnixEpoch: bigint };
  lastActivityAt: { microsSinceUnixEpoch: bigint };
  validated: boolean;
  preferredWorldId: bigint;
}

/** Structural shape of an `orbit_member` row (P6 Orbits). */
interface OrbitMemberRowLike {
  memberId: bigint;
  orbitId: bigint;
  identity: { toHexString(): string };
  joinedAt: { microsSinceUnixEpoch: bigint };
}

/** Structural shape of a `need_satisfier` row (P6 venues). */
interface NeedSatisfierRowLike {
  worldId: bigint;
  need: string;
  ratePerMin: number;
}

/** Structural shape of a `wave` row (P6 ice-breaker). */
interface WaveRowLike {
  waveId: bigint;
  fromIdentity: { toHexString(): string };
  toIdentity: { toHexString(): string };
  boosted: boolean;
  at: { microsSinceUnixEpoch: bigint };
}

/** Structural shape of a `reputation` row (P7 — four explicit scores). */
interface ReputationRowLike {
  identity: { toHexString(): string };
  trustworthiness: number;
  creatorRatingX100: number;
  ratingCount: number;
  socialCapital: number;
  notoriety: number;
  traitorUntil: { microsSinceUnixEpoch: bigint };
}

/** Structural shape of a `vouch` row (P7 — a staked trust edge). */
interface VouchRowLike {
  vouchId: bigint;
  voucher: { toHexString(): string };
  vouchee: { toHexString(): string };
  infStaked: bigint;
  weightX100: number;
  at: { microsSinceUnixEpoch: bigint };
  active: boolean;
}

/** Structural shape of a `review` row (P7 — star rating or Caution flag). */
interface ReviewRowLike {
  reviewId: bigint;
  subject: { toHexString(): string };
  reviewer: { toHexString(): string };
  stars: number;
  isCaution: boolean;
  ref: string;
  at: { microsSinceUnixEpoch: bigint };
}

/** Structural shape of a `betrayal_record` row (P7 — proven collab betrayal). */
interface BetrayalRecordRowLike {
  recordId: bigint;
  betrayer: { toHexString(): string };
  victim: { toHexString(): string };
  kind: string;
  worldId: bigint;
  at: { microsSinceUnixEpoch: bigint };
}

function pulseRowToShared(row: PulseRowLike): PulseState {
  return {
    identity: row.identity.toHexString(),
    social: row.social,
    fun: row.fun,
    creativity: row.creativity,
    rest: row.rest,
    visSocial: row.visSocial,
    visFun: row.visFun,
    visCreativity: row.visCreativity,
    visRest: row.visRest,
    updatedAt: row.updatedAt.microsSinceUnixEpoch,
  };
}

function compassRowToShared(row: CompassRowLike): CompassState {
  return {
    identity: row.identity.toHexString(),
    want: row.want,
    fear: row.fear,
    rotatedAt: row.rotatedAt.microsSinceUnixEpoch,
    public: row.public,
  };
}

function orbitRowToShared(row: OrbitRowLike): OrbitState {
  return {
    orbitId: row.orbitId,
    createdAt: row.createdAt.microsSinceUnixEpoch,
    lastActivityAt: row.lastActivityAt.microsSinceUnixEpoch,
    validated: row.validated,
    preferredWorldId: row.preferredWorldId,
  };
}

function orbitMemberRowToShared(row: OrbitMemberRowLike): OrbitMemberState {
  return {
    memberId: row.memberId,
    orbitId: row.orbitId,
    identity: row.identity.toHexString(),
    joinedAt: row.joinedAt.microsSinceUnixEpoch,
  };
}

function needSatisfierRowToShared(row: NeedSatisfierRowLike): NeedSatisfier {
  return {
    worldId: row.worldId,
    need: row.need as NeedName,
    ratePerMin: row.ratePerMin,
  };
}

function reputationRowToShared(row: ReputationRowLike): ReputationScores {
  return {
    identity: row.identity.toHexString(),
    trustworthiness: row.trustworthiness,
    creatorRatingX100: row.creatorRatingX100,
    ratingCount: row.ratingCount,
    socialCapital: row.socialCapital,
    notoriety: row.notoriety,
    traitorUntil: row.traitorUntil.microsSinceUnixEpoch,
  };
}

function vouchRowToShared(row: VouchRowLike): VouchEdge {
  return {
    vouchId: row.vouchId,
    voucher: row.voucher.toHexString(),
    vouchee: row.vouchee.toHexString(),
    infStaked: row.infStaked,
    weightX100: row.weightX100,
    at: row.at.microsSinceUnixEpoch,
    active: row.active,
  };
}

function reviewRowToShared(row: ReviewRowLike): Review {
  return {
    reviewId: row.reviewId,
    subject: row.subject.toHexString(),
    reviewer: row.reviewer.toHexString(),
    stars: row.stars,
    isCaution: row.isCaution,
    ref: row.ref,
    at: row.at.microsSinceUnixEpoch,
  };
}

function betrayalRowToShared(row: BetrayalRecordRowLike): BetrayalRecord {
  return {
    recordId: row.recordId,
    betrayer: row.betrayer.toHexString(),
    victim: row.victim.toHexString(),
    kind: row.kind,
    worldId: row.worldId,
    at: row.at.microsSinceUnixEpoch,
  };
}

/** Structural shape of an `event` row (P8 — a scheduled gathering). */
interface EventRowLike {
  eventId: bigint;
  host: { toHexString(): string };
  worldId: bigint;
  title: string;
  theme: string;
  startAt: { microsSinceUnixEpoch: bigint };
  recurrence: string;
  ticketPriceSov: bigint;
  attendanceTarget: number;
  checkedInCount: number;
  hostInfPaid: boolean;
  hostBonusPaid: boolean;
  memorialCreated: boolean;
  createdAt: { microsSinceUnixEpoch: bigint };
}

/** Structural shape of an `event_attendance` row (P8 — one person's relationship to an event). */
interface EventAttendanceRowLike {
  attendanceId: bigint;
  eventId: bigint;
  identity: { toHexString(): string };
  escrowId: bigint;
  sessionsAttended: number;
  checkedIn: boolean;
  lastSessionAt: { microsSinceUnixEpoch: bigint };
  satisfactionRecorded: boolean;
  satisfaction: number;
  invitedAt: { microsSinceUnixEpoch: bigint };
  firstSeenAt: { microsSinceUnixEpoch: bigint };
}

/** Structural shape of a `ritual` row (P8 — a recurring event's institutionalization state). */
interface RitualRowLike {
  ritualId: bigint;
  eventId: bigint;
  repetitions: number;
  institutionalized: boolean;
  npcHostId: bigint;
  lastSessionAt: { microsSinceUnixEpoch: bigint };
  humansThisSession: number;
  humansLastSession: number;
}

/** Structural shape of a `ritual_mute` row (P8 — a per-(ritual, identity) invite opt-out). */
interface RitualMuteRowLike {
  muteId: bigint;
  ritualId: bigint;
  identity: { toHexString(): string };
}

/** Structural shape of an `npc_host` row (P8 — a deterministic ritual host). */
interface NpcHostRowLike {
  npcId: bigint;
  worldId: bigint;
  ritualId: bigint;
  kind: string;
  memory: string;
  createdAt: { microsSinceUnixEpoch: bigint };
}

/** Structural shape of a `memory_entry` row (P8 — one Memory Book entry). */
interface MemoryEntryRowLike {
  entryId: bigint;
  kind: string;
  ref: string;
  author: { toHexString(): string };
  text: string;
  mediaRef: string;
  permanent: boolean;
  expiresAt: { microsSinceUnixEpoch: bigint };
  worldId: bigint;
  at: { microsSinceUnixEpoch: bigint };
}

function eventRowToShared(row: EventRowLike): EventSpec {
  return {
    eventId: row.eventId,
    host: row.host.toHexString(),
    worldId: row.worldId,
    title: row.title,
    theme: row.theme,
    startAt: row.startAt.microsSinceUnixEpoch,
    recurrence: row.recurrence,
    ticketPriceSov: row.ticketPriceSov,
    attendanceTarget: row.attendanceTarget,
    checkedInCount: row.checkedInCount,
    hostInfPaid: row.hostInfPaid,
    hostBonusPaid: row.hostBonusPaid,
    memorialCreated: row.memorialCreated,
    createdAt: row.createdAt.microsSinceUnixEpoch,
  };
}

function attendanceRowToShared(row: EventAttendanceRowLike): EventAttendance {
  return {
    attendanceId: row.attendanceId,
    eventId: row.eventId,
    identity: row.identity.toHexString(),
    escrowId: row.escrowId,
    sessionsAttended: row.sessionsAttended,
    checkedIn: row.checkedIn,
    lastSessionAt: row.lastSessionAt.microsSinceUnixEpoch,
    satisfactionRecorded: row.satisfactionRecorded,
    satisfaction: row.satisfaction,
    invitedAt: row.invitedAt.microsSinceUnixEpoch,
    firstSeenAt: row.firstSeenAt.microsSinceUnixEpoch,
  };
}

function ritualRowToShared(row: RitualRowLike): RitualState {
  return {
    ritualId: row.ritualId,
    eventId: row.eventId,
    repetitions: row.repetitions,
    institutionalized: row.institutionalized,
    npcHostId: row.npcHostId,
    lastSessionAt: row.lastSessionAt.microsSinceUnixEpoch,
    humansThisSession: row.humansThisSession,
    humansLastSession: row.humansLastSession,
  };
}

function ritualMuteRowToShared(row: RitualMuteRowLike): RitualMute {
  return {
    muteId: row.muteId,
    ritualId: row.ritualId,
    identity: row.identity.toHexString(),
  };
}

function npcHostRowToShared(row: NpcHostRowLike): NpcHost {
  return {
    npcId: row.npcId,
    worldId: row.worldId,
    ritualId: row.ritualId,
    kind: row.kind,
    memory: row.memory,
    createdAt: row.createdAt.microsSinceUnixEpoch,
  };
}

function memoryEntryRowToShared(row: MemoryEntryRowLike): MemoryEntry {
  return {
    entryId: row.entryId,
    kind: row.kind as MemoryEntryKind,
    ref: row.ref,
    author: row.author.toHexString(),
    text: row.text,
    mediaRef: row.mediaRef,
    permanent: row.permanent,
    expiresAt: row.expiresAt.microsSinceUnixEpoch,
    worldId: row.worldId,
    at: row.at.microsSinceUnixEpoch,
  };
}

/** Structural shape of a `group` row (P9 — a player-formed organization). */
interface GroupRowLike {
  groupId: bigint;
  name: string;
  charterRef: string;
  hallWorldId: bigint;
  treasurySov: bigint;
  state: string;
  reviewUntil: { microsSinceUnixEpoch: bigint };
  createdAt: { microsSinceUnixEpoch: bigint };
}

/** Structural shape of a `group_member` row (P9). */
interface GroupMemberRowLike {
  memberId: bigint;
  groupId: bigint;
  identity: { toHexString(): string };
  role: string;
  joinedAt: { microsSinceUnixEpoch: bigint };
}

/** Structural shape of a `group_signature` row (P9). */
interface GroupSignatureRowLike {
  signatureId: bigint;
  groupId: bigint;
  signer: { toHexString(): string };
  at: { microsSinceUnixEpoch: bigint };
}

/** Structural shape of a `charter_challenge` row (P9). */
interface CharterChallengeRowLike {
  challengeId: bigint;
  groupId: bigint;
  challenger: { toHexString(): string };
  reason: string;
  at: { microsSinceUnixEpoch: bigint };
}

/** Structural shape of a `treasury_spend` row (P9). */
interface TreasurySpendRowLike {
  spendId: bigint;
  groupId: bigint;
  officer: { toHexString(): string };
  amountSov: bigint;
  purpose: string;
  requiresVote: boolean;
  approved: boolean;
  executed: boolean;
  voteClosesAt: { microsSinceUnixEpoch: bigint };
  at: { microsSinceUnixEpoch: bigint };
}

/** Structural shape of a `spend_vote` row (P9). */
interface SpendVoteRowLike {
  voteId: bigint;
  spendId: bigint;
  voter: { toHexString(): string };
  yes: boolean;
  at: { microsSinceUnixEpoch: bigint };
}

/** Structural shape of a `fork_vote` row (P9). */
interface ForkVoteRowLike {
  forkId: bigint;
  groupId: bigint;
  initiator: { toHexString(): string };
  faction: string;
  openedAt: { microsSinceUnixEpoch: bigint };
  closesAt: { microsSinceUnixEpoch: bigint };
  passed: boolean;
  resolved: boolean;
}

/** Structural shape of a `fork_ballot` row (P9). */
interface ForkBallotRowLike {
  ballotId: bigint;
  forkId: bigint;
  voter: { toHexString(): string };
  yes: boolean;
  at: { microsSinceUnixEpoch: bigint };
}

function groupRowToShared(row: GroupRowLike): Group {
  return {
    groupId: row.groupId,
    name: row.name,
    charterRef: row.charterRef,
    hallWorldId: row.hallWorldId,
    treasurySov: row.treasurySov,
    state: row.state as GroupState,
    reviewUntil: row.reviewUntil.microsSinceUnixEpoch,
    createdAt: row.createdAt.microsSinceUnixEpoch,
  };
}

function groupMemberRowToShared(row: GroupMemberRowLike): GroupMember {
  return {
    memberId: row.memberId,
    groupId: row.groupId,
    identity: row.identity.toHexString(),
    role: row.role as GroupRole,
    joinedAt: row.joinedAt.microsSinceUnixEpoch,
  };
}

function groupSignatureRowToShared(row: GroupSignatureRowLike): GroupSignature {
  return {
    signatureId: row.signatureId,
    groupId: row.groupId,
    signer: row.signer.toHexString(),
    at: row.at.microsSinceUnixEpoch,
  };
}

function charterChallengeRowToShared(row: CharterChallengeRowLike): CharterChallenge {
  return {
    challengeId: row.challengeId,
    groupId: row.groupId,
    challenger: row.challenger.toHexString(),
    reason: row.reason,
    at: row.at.microsSinceUnixEpoch,
  };
}

function treasurySpendRowToShared(row: TreasurySpendRowLike): TreasurySpend {
  return {
    spendId: row.spendId,
    groupId: row.groupId,
    officer: row.officer.toHexString(),
    amountSov: row.amountSov,
    purpose: row.purpose,
    requiresVote: row.requiresVote,
    approved: row.approved,
    executed: row.executed,
    voteClosesAt: row.voteClosesAt.microsSinceUnixEpoch,
    at: row.at.microsSinceUnixEpoch,
  };
}

function spendVoteRowToShared(row: SpendVoteRowLike): SpendVote {
  return {
    voteId: row.voteId,
    spendId: row.spendId,
    voter: row.voter.toHexString(),
    yes: row.yes,
    at: row.at.microsSinceUnixEpoch,
  };
}

function forkVoteRowToShared(row: ForkVoteRowLike): ForkVote {
  return {
    forkId: row.forkId,
    groupId: row.groupId,
    initiator: row.initiator.toHexString(),
    faction: row.faction,
    openedAt: row.openedAt.microsSinceUnixEpoch,
    closesAt: row.closesAt.microsSinceUnixEpoch,
    passed: row.passed,
    resolved: row.resolved,
  };
}

function forkBallotRowToShared(row: ForkBallotRowLike): ForkBallot {
  return {
    ballotId: row.ballotId,
    forkId: row.forkId,
    voter: row.voter.toHexString(),
    yes: row.yes,
    at: row.at.microsSinceUnixEpoch,
  };
}

export class AtlasNetworkModule implements IModule {
  /**
   * Per-topic trailing debounce (120 ms) for row-callback fan-outs. WHY: a
   * global table's initial sync replayed N rows into N full panel re-renders
   * (reputation → 4 modules, groups → list spread+sort, …) — the join-freeze
   * class the structure panels already coalesce (2026-09-12).
   */
  private readonly topicTimers = new Map<string, number>();
  private pingTopic(topic: 'atlas:reputation-updated' | 'atlas:events-updated' | 'atlas:groups-updated'): void {
    if (this.topicTimers.has(topic)) return;
    this.topicTimers.set(topic, window.setTimeout(() => { this.topicTimers.delete(topic); bus.emit(topic, {}); }, 120));
  }
  readonly name = 'AtlasNetwork';

  private identityModule: AtlasIdentityModule;
  private baseSub: SubscriptionHandle | null = null;
  private aoiSub: SubscriptionHandle | null = null;
  private versonaAoiSub: SubscriptionHandle | null = null;
  /** Cell the current AOI window is centered on. */
  /** One-time identity-scoped self player_state subscription (login-resume). */
  private selfRowSubbed = false;
  private aoiCenterX = 0;
  /** Monotonic world-entry generation (a newer hop cancels an older continuation). */
  private entryGen = 0;
  /** Generation of the hop whose reducer round trip / resume wait is still in flight (0 = none). */
  private hopInFlightGen = 0;
  private aoiCenterZ = 0;
  private tableCallbacksBound = false;
  /** Unsubscribe handles for the four bus.on() registrations in init(). */
  private busUnsubs: Array<() => void> = [];

  constructor(identityModule: AtlasIdentityModule) {
    this.identityModule = identityModule;
  }

  init(app: App): void {
    // Establish the connection. onConnect fires asynchronously.
    const conn = connect({
      onConnect: (c, identity) => this.handleConnect(c, identity.toHexString()),
      onConnectError: (err) => bus.emit('atlas:status', { text: `connect error: ${err.message}` }),
      onDisconnect: () => this.onConnectionLost(),
    });
    atlas.conn = conn;
    bus.emit('atlas:status', { text: 'connecting…' });

    // Enter the Commons once the passport is ready.
    // Store unsubscribe handles so dispose() can remove all four listeners.
    this.busUnsubs = [
      // Main-menu gate: while the boot menu is in the DOM the PLAYER chooses
      // when (and what) to enter — the auto-enter fires on atlas:menu-enter
      // instead. Harness/embed pages ship no menu → the old flow unchanged.
      bus.on('atlas:account-ready', () => {
        // RECONNECT = RELOAD + RESUME: a fresh record means this page load is
        // a reconnect (or a reload within two minutes) — skip the menu and the
        // traveler screens and go straight back into that world; the login
        // RESUME law puts the avatar back where it stood.
        const resume = pendingResume();
        if (resume) {
          clearResume();
          bus.emit('atlas:status', { text: 'reconnected — resuming' });
          bus.emit('atlas:menu-enter', { worldId: BigInt(resume.worldId) });
          return;
        }
        if (document.getElementById('main-menu')) return;
        this.enterCommons();
      }),
      bus.on('atlas:menu-enter', ({ worldId }) => {
        // Two-phase homepage: the world modules boot ONLY once the player
        // commits (atlas.ensureWorldBoot, idempotent — usually already
        // running since atlas:world-picked). Entry waits for them.
        void (async () => {
          await atlas.ensureWorldBoot?.();
          if (worldId !== null) void this.enterWorld(worldId);
          else this.enterCommons();
        })();
      }),
      bus.on('atlas:toggle-world', () => this.toggleWorld()),
      bus.on('atlas:enter-world', ({ worldId }) => void this.enterWorld(worldId)),
      bus.on('atlas:world-adopted', ({ worldId, x, z }) => this.adoptWorld(worldId, x, z)),
      bus.on('atlas:place-requested', () => this.placeObject()),
    ];
    // Probe door (localhost / atlas:debug only): drive the REAL enter-world
    // path from a headless gate — world-swap GPU reproduction, 2026-08-24.
    try {
      if (location.hostname === 'localhost' || location.hostname === '127.0.0.1' || localStorage.getItem('atlas:debug') === '1') {
        (window as unknown as Record<string, unknown>).__enterWorld = (id: string): boolean => {
          try { void this.enterWorld(BigInt(id)); return true; } catch { return false; }
        };
      }
    } catch { /* no window/storage */ }

    // AOI maintenance runs in FixedSim (after movement integrates the local
    // cell). Re-subscribes only when the player crosses the hysteresis band.
    app.world.addSystem(
      makeSystem('AtlasAOI', Phase.FixedSim, 80, () => this.maintainAoi()),
    );
  }

  // ─── Connection ────────────────────────────────────────────────────────────

  private handleConnect(conn: DbConnection, identityHex: string): void {
    bus.emit('atlas:status', { text: 'connected' });

    // Resolve the local identity synchronously — it's known at connect time.
    // The base subscription's per-row onInsert callbacks can fire BEFORE its
    // onApplied (where the identity module also stamps this), so on a reconnect
    // with an existing wallet/inventory this is what lets the identity-gated
    // handlers (wallet/asset/inventory) recognize the local player's own rows.
    atlas.identityHex = identityHex;

    // Subscribe to the small public tables that drive identity + world
    // resolution. The avatar + display-name tables are global (low cardinality,
    // one row per account) so portable identity is always available for any
    // visible player. AOI is layered on top once we enter a world.
    // Resolve commons/personal world ids the MOMENT rows replicate — never
    // lazily on first entry. While both ids sat null (the whole main-menu
    // phase), every `currentWorldId !== commonsWorldId` gate inverted
    // (null !== null is false) and the commons modules streamed + compiled
    // the entire hub city BEHIND the menu; entering a world then froze on
    // that pipeline backlog (the personal-world freeze, 2026-07-30).
    conn.db.world.onInsert(() => this.resolveWorlds(conn));
    this.baseSub = conn
      .subscriptionBuilder()
      .onApplied(() => {
        this.resolveWorlds(conn);
        this.identityModule.onConnected(conn, identityHex);
      })
      .subscribe([
        'SELECT * FROM account',
        'SELECT * FROM avatar',
        'SELECT * FROM display_name_change',
        'SELECT * FROM world',
        'SELECT * FROM asset',
        // The LOD-variant catalog (~800 static rows). NEVER subscribed before
        // 2026-07-30: conn.db.renderAssetVariant was silently EMPTY, so every
        // loadVariant/loadForTier fell back to the canonical LOD0 — the potato
        // city walk streamed 21 MB × 16×2048²-texture GLBs it never rendered.
        'SELECT * FROM render_asset_variant',
        // Texture tiers (settlement study R1): mip-truncated copies of each LOD
        // file; AssetModule picks the one ≤ the preset's maxTextureRes BEFORE it
        // fetches. Static, a few hundred rows.
        'SELECT * FROM render_asset_texture_tier',
        // Asset packs (settlement study R6): a handful of rows mapping member
        // keys onto one .vpak each; AssetModule opens a pack once per session.
        'SELECT * FROM asset_pack',
        // One row per pilot whose ship stands LANDED somewhere (the vehicle
        // wave) — tiny, and every client renders every parked hull it can see.
        'SELECT * FROM ship_berth',
        // Deployed utility droids (≤3 per owner) — every client renders them.
        'SELECT * FROM droid_unit',
        // Personal map waypoints — own rows only (planet-scoped client-side).
        'SELECT * FROM waypoint WHERE owner = :sender',
        // Self-only tables ride `:sender` (2026-09-12): every reader filters to the
        // local identity, so the global sync replicated every player's rows for
        // nothing; provenance_event had no reader at all.
        'SELECT * FROM inventory_item WHERE owner = :sender',
        'SELECT * FROM wallet',
        'SELECT * FROM market_listing',
        // The Forge (P4): the Permission Matrix + per-world governance. Low
        // cardinality public tables — the global subscribe keeps role/quarantine
        // state available for whichever world the player is currently in.
        'SELECT * FROM world_role',
        // One row per traveler — the Merchant Hive Return chip needs it
        // mirrored from boot (a phase-2 subscribe rides the saturated
        // post-entry socket and can starve; this base set goes out calm).
        'SELECT * FROM travel_return',
        'SELECT * FROM world_governance',
        'SELECT * FROM world_co_author',
        // World Logic (P5): Behavioural Contracts + their deterministic state and
        // replicated event trail. Low cardinality public tables — every client
        // sees the same contracts, throttle state, and event log for a world.
        'SELECT * FROM contract',
        'SELECT * FROM contract_state',
        'SELECT * FROM contract_event',
        // Social Pulse / Compass / Orbits (P6): the social-physics layer. All
        // low-cardinality public tables (one row per account / venue / group),
        // so a global subscribe keeps an avatar's legible state, the visible
        // Compasses, the venue satisfiers, and the Orbit graph available
        // everywhere — Orbits in particular must survive a world hop.
        'SELECT * FROM pulse',
        'SELECT * FROM compass',
        'SELECT * FROM need_satisfier',
        'SELECT * FROM wave',
        'SELECT * FROM orbit',
        'SELECT * FROM orbit_member',
        // Reputation & Trust (P7): the four explicit scores + the trust graph.
        // All low-cardinality public tables — a global subscribe keeps every
        // visible player's standing, the vouch edges (so the local player sees
        // who they staked on and who staked on them), the reviews, and the
        // betrayal records available everywhere. Reputation is server-computed;
        // the client never writes a score.
        'SELECT * FROM reputation',
        'SELECT * FROM vouch',
        'SELECT * FROM review',
        'SELECT * FROM betrayal_record',
        // Rituals, Events & Memory Book (P8): the ritual engine + the public
        // archive. All low-cardinality public tables — a global subscribe keeps
        // every scheduled event, the local player's attendance/ticket state, the
        // ritual institutionalization rows, the per-group invite mutes, the
        // deterministic NPC hosts (with their structured regular-memory), and
        // every Memory Book entry available everywhere. Counts, payouts, and host
        // memory are server-computed; the client never writes one.
        'SELECT * FROM event',
        'SELECT * FROM event_attendance',
        'SELECT * FROM ritual',
        'SELECT * FROM ritual_mute',
        'SELECT * FROM npc_host',
        'SELECT * FROM memory_entry',
        // Groups, Charters, Treasuries & Forks (P9): the organizational layer.
        // All low-cardinality public tables — a global subscribe keeps every
        // group, its membership + charter signatures, any standing challenge,
        // the Treasury spend proposals + their ballots, and the open/settled
        // forks + their ballots available everywhere (a group survives a world
        // hop, and the Hall is a P4 world the member may not currently be in).
        // The Treasury balance MIRRORS the P3 ledger; spend approval, the
        // embezzlement penalty, and a fork's proportional split are all
        // server-computed. The client never writes one.
        'SELECT * FROM "group"',
        'SELECT * FROM group_member',
        'SELECT * FROM group_signature',
        'SELECT * FROM charter_challenge',
        'SELECT * FROM treasury_spend',
        'SELECT * FROM spend_vote',
        'SELECT * FROM fork_vote',
        'SELECT * FROM fork_ballot',
        // ── The Creator Exchange & Monetization (P10) ──
        // Replicated into the client cache so CreatorExchangeModule can read its
        // own rows (`conn.db.*`) + drive reducers. Every split/cap/consequence is
        // server-authoritative; the client only renders + calls.
        'SELECT * FROM subscription',
        'SELECT * FROM game_pass',
        'SELECT * FROM game_pass_owner',
        'SELECT * FROM rental',
        'SELECT * FROM tip_record',
        'SELECT * FROM royalty',
        'SELECT * FROM creator_cert',
        'SELECT * FROM entitlement WHERE identity = :sender',
        'SELECT * FROM daily_settlement',
        // ── Discovery & The Social Feed (P11) ──
        // The materialized algorithmic feed + per-world metrics + ratings + the
        // algo opt-out. The Social feed is derived client-side from the already-
        // subscribed orbit/vouch tables; world_visit stays server-side.
        'SELECT * FROM world_metric',
        'SELECT * FROM world_rating',
        'SELECT * FROM discovery_prefs',
        'SELECT * FROM feed_rank',
        // ── The Chronicler, Seasons & Live Ops (P12) ──
        'SELECT * FROM season',
        'SELECT * FROM chronicler_event',
        'SELECT * FROM presence',
        'SELECT * FROM council_term',
        'SELECT * FROM council_vote',
        'SELECT * FROM council_seat',
        'SELECT * FROM council_action',
        'SELECT * FROM legacy_grant',
        'SELECT * FROM exploit_action',
        // ── The First Hour — Onboarding & Churn Parachutes (P13) ──
        'SELECT * FROM onboarding WHERE identity = :sender',
        'SELECT * FROM guide_message',
        'SELECT * FROM beginner_mixer_suggestion',
        // ── World Model & Building — SWG Structures (P14.1) ──
        // The player's lot budget, deeds, deployed structures, interior
        // decorations, access lists, and no-build zones. Public tables; the
        // StructureModule reads them straight from the cache + drives the reducers.
        'SELECT * FROM lot_budget',
        'SELECT * FROM structure_deed',
        'SELECT * FROM versona_listing', // merchant stalls (bazaar terminal)
        // Merchant slice (2026-09-15): the counter — who is served, who waits, what is held, the receipts.
        'SELECT * FROM merchant_service', 'SELECT * FROM merchant_queue', 'SELECT * FROM merchant_hold', 'SELECT * FROM merchant_receipt',
        // Disposition (2026-09-15): my own relation events — the crowd's eyes and the greeting read the four axes.
        'SELECT * FROM player_relation_event WHERE identity = :sender',
        'SELECT * FROM structure',
        'SELECT * FROM structure_item',
        'SELECT * FROM structure_permission',
        'SELECT * FROM no_build_zone',
        // ── World Model & Building — SWG Player Cities (P14.2) ──
        'SELECT * FROM city',
        'SELECT * FROM citizen',
        'SELECT * FROM civic_structure',
        'SELECT * FROM zoning_grant',
        'SELECT * FROM mayor_election',
        'SELECT * FROM mayor_vote',
        // ── World Model & Building — Community-World Environment (P14.3) ──
        'SELECT * FROM terrain_region',
        'SELECT * FROM sky_state',
        'SELECT * FROM versona',
        'SELECT * FROM instance_group', // G2: declared foliage scatter regions
        // ── World Model & Building — Freeform SL Building (P14.4) ──
        'SELECT * FROM world_build_budget',
        'SELECT * FROM part',
        'SELECT * FROM linkset',
        'SELECT * FROM part_script',
        // ── Forge Mode — F1 Authoring ──
        'SELECT * FROM forge_project',
        'SELECT * FROM forge_scene',
        'SELECT * FROM forge_instance',
        'SELECT * FROM forge_script',
      ]);

    this.bindTableCallbacks(conn);
  }

  /** Wire row callbacks once. Remote avatars + placed objects become state. */
  private bindTableCallbacks(conn: DbConnection): void {
    if (this.tableCallbacksBound) return;
    this.tableCallbacksBound = true;

    // Presence flips: offline → drop the remote now; online → re-run the cached
    // player_state row (it may not re-emit for an idle just-connected player).
    // player_state is keyed by identity (PK btree): resolve the one row by index
    // rather than scanning + hex-encoding every cached row per presence flip
    // (presence is a GLOBAL subscription — every login/logout platform-wide fired
    // an O(players) scan here).
    conn.db.presence.onUpdate((_ctx, _old, p) => {
      const hex = p.identity.toHexString();
      if (!p.online) { atlas.remotes.delete(hex); return; }
      const ps = conn.db.playerState.identity.find(p.identity);
      if (ps) this.onPlayer(conn, ps);
    });
    conn.db.presence.onInsert((_ctx, p) => {
      if (!p.online) return;
      const ps = conn.db.playerState.identity.find(p.identity);
      if (ps) this.onPlayer(conn, ps);
    });
    conn.db.playerState.onInsert((_ctx, row) => this.onPlayer(conn, row));
    conn.db.playerState.onUpdate((_ctx, _old, row) => this.onPlayer(conn, row));
    conn.db.playerState.onDelete((_ctx, row) => {
      const hex = row.identity.toHexString();
      if (hex === atlas.identityHex) return;
      atlas.remotes.delete(hex);
    });

    conn.db.worldObject.onInsert((_ctx, row) => {
      if (atlas.currentWorldId !== null && row.worldId === atlas.currentWorldId) {
        bus.emit('atlas:object-added', {
          objectId: row.objectId,
          prefabId: row.prefabId,
          x: row.posX,
          y: row.posY,
          z: row.posZ,
          rotX: row.rotX,
          rotY: row.rotY,
          rotZ: row.rotZ,
          rotW: row.rotW,
          scale: row.scale,
        });
      }
    });
    conn.db.worldObject.onDelete((_ctx, row) => {
      bus.emit('atlas:object-removed', { objectId: row.objectId });
    });

    // Portable identity: avatar appearance + authoritative complexity. The
    // global `avatar` table means a write propagates to every client instantly.
    conn.db.avatar.onInsert((_ctx, row) => this.onAvatar(row));
    conn.db.avatar.onUpdate((_ctx, _old, row) => this.onAvatar(row));

    // Provenance System: the global `asset` graph + the local inventory items.
    conn.db.asset.onInsert((_ctx, row) => this.onAsset(row));
    conn.db.asset.onUpdate((_ctx, _old, row) => this.onAsset(row));
    conn.db.inventoryItem.onInsert((_ctx, row) => this.onInventoryItem(row));
    conn.db.inventoryItem.onUpdate((_ctx, _old, row) => this.onInventoryItem(row));
    conn.db.inventoryItem.onDelete((_ctx, row) => {
      atlas.inventory.delete(row.itemId.toString());
      bus.emit('atlas:inventory-updated', {});
    });

    // Economy: the local player's wallet (HUD) + the global marketplace listings.
    conn.db.wallet.onInsert((_ctx, row) => this.onWallet(row));
    conn.db.wallet.onUpdate((_ctx, _old, row) => this.onWallet(row));
    conn.db.marketListing.onInsert((_ctx, row) => this.onListing(row));
    conn.db.marketListing.onUpdate((_ctx, _old, row) => this.onListing(row));
    conn.db.marketListing.onDelete((_ctx, row) => {
      atlas.listings.delete(row.listingId.toString());
      bus.emit('atlas:listings-updated', {});
    });

    // Self display-name updates (from changeDisplayName) refresh the HUD label.
    conn.db.account.onUpdate((_ctx, _old, row) => {
      if (atlas.identityHex && row.identity.toHexString() === atlas.identityHex) {
        atlas.username = row.username;
        atlas.displayName = row.displayName;
        bus.emit('atlas:displayname-updated', {
          displayName: row.displayName,
          lastChangedMicros: atlas.displayNameLastChangedMicros,
        });
      }
    });

    // Weekly-cooldown ledger — track the last change time for the editor.
    conn.db.displayNameChange.onInsert((_ctx, row) => this.onNameChange(row));
    conn.db.displayNameChange.onUpdate((_ctx, _old, row) => this.onNameChange(row));

    // Social Pulse (P6): the local player's four needs drive the always-on HUD.
    conn.db.pulse.onInsert((_ctx, row) => this.onPulse(row));
    conn.db.pulse.onUpdate((_ctx, _old, row) => this.onPulse(row));
    conn.db.pulse.onDelete((_ctx, row) => {
      if (atlas.identityHex && row.identity.toHexString() === atlas.identityHex) {
        atlas.localPulse = null;
        bus.emit('atlas:pulse-updated', {});
      }
    });

    // Social Compass (P6): every visible Want/Fear pair (self + strangers).
    conn.db.compass.onInsert((_ctx, row) => this.onCompass(row));
    conn.db.compass.onUpdate((_ctx, _old, row) => this.onCompass(row));
    conn.db.compass.onDelete((_ctx, row) => {
      atlas.compasses.delete(row.identity.toHexString());
      bus.emit('atlas:compass-updated', {});
    });

    // Venue satisfiers (P6): which need a world lifts, for the HUD's place hint.
    conn.db.needSatisfier.onInsert((_ctx, row) => this.onNeedSatisfier(row));
    conn.db.needSatisfier.onUpdate((_ctx, _old, row) => this.onNeedSatisfier(row));
    conn.db.needSatisfier.onDelete((_ctx, row) => {
      atlas.needSatisfiers.delete(row.worldId.toString());
      bus.emit('atlas:pulse-updated', {});
    });

    // Orbits (P6): the transient proximity groups + their membership.
    conn.db.orbit.onInsert((_ctx, row) => this.onOrbit(row));
    conn.db.orbit.onUpdate((_ctx, _old, row) => this.onOrbit(row));
    conn.db.orbit.onDelete((_ctx, row) => {
      atlas.orbits.delete(row.orbitId.toString());
      bus.emit('atlas:orbit-updated', {});
    });
    conn.db.orbitMember.onInsert((_ctx, row) => this.onOrbitMember(row));
    conn.db.orbitMember.onUpdate((_ctx, _old, row) => this.onOrbitMember(row));
    conn.db.orbitMember.onDelete((_ctx, row) => {
      atlas.orbitMembers.delete(row.memberId.toString());
      bus.emit('atlas:orbit-updated', {});
    });

    // Waves (P6): a Wave aimed at the local player surfaces as a transient
    // status flash — an ice-breaker greeting, never a notification the player
    // must act on. The Social-need boost it grants arrives via the `pulse` row.
    conn.db.wave.onInsert((_ctx, row) => this.onWave(row));

    // Reputation & Trust (P7): the four explicit scores + the vouch / review /
    // betrayal graph. The client only mirrors these; the server owns every
    // score. A reputation row is keyed by identity, so deletes never happen in
    // practice (scores persist), but vouches flip `active` on revoke/forfeit.
    conn.db.reputation.onInsert((_ctx, row) => this.onReputation(row));
    conn.db.reputation.onUpdate((_ctx, _old, row) => this.onReputation(row));
    conn.db.vouch.onInsert((_ctx, row) => this.onVouch(row));
    conn.db.vouch.onUpdate((_ctx, _old, row) => this.onVouch(row));
    conn.db.vouch.onDelete((_ctx, row) => {
      atlas.vouches.delete(row.vouchId.toString());
      this.pingTopic('atlas:reputation-updated');
    });
    conn.db.review.onInsert((_ctx, row) => this.onReview(row));
    conn.db.review.onUpdate((_ctx, _old, row) => this.onReview(row));
    conn.db.review.onDelete((_ctx, row) => {
      atlas.reviews.delete(row.reviewId.toString());
      this.pingTopic('atlas:reputation-updated');
    });
    conn.db.betrayalRecord.onInsert((_ctx, row) => this.onBetrayal(row));
    conn.db.betrayalRecord.onUpdate((_ctx, _old, row) => this.onBetrayal(row));

    // Rituals, Events & Memory Book (P8): the ritual engine + the public
    // archive. The client only mirrors these; the server owns every count,
    // payout, and host memory. Events/rituals/attendance UPDATE in place
    // (check-ins bump counts, institutionalization flips a flag), so insert and
    // update share one handler; deletes never happen (the archive is additive).
    conn.db.event.onInsert((_ctx, row) => this.onEvent(row));
    conn.db.event.onUpdate((_ctx, _old, row) => this.onEvent(row));
    conn.db.eventAttendance.onInsert((_ctx, row) => this.onAttendance(row));
    conn.db.eventAttendance.onUpdate((_ctx, _old, row) => this.onAttendance(row));
    conn.db.ritual.onInsert((_ctx, row) => this.onRitual(row));
    conn.db.ritual.onUpdate((_ctx, _old, row) => this.onRitual(row));
    conn.db.ritualMute.onInsert((_ctx, row) => this.onRitualMute(row));
    conn.db.ritualMute.onDelete((_ctx, row) => {
      atlas.ritualMutes.delete(row.muteId.toString());
      this.pingTopic('atlas:events-updated');
    });
    conn.db.npcHost.onInsert((_ctx, row) => this.onNpcHost(row));
    conn.db.npcHost.onUpdate((_ctx, _old, row) => this.onNpcHost(row));
    conn.db.memoryEntry.onInsert((_ctx, row) => this.onMemoryEntry(row));
    conn.db.memoryEntry.onUpdate((_ctx, _old, row) => this.onMemoryEntry(row));
    conn.db.memoryEntry.onDelete((_ctx, row) => {
      atlas.memoryEntries.delete(row.entryId.toString());
      bus.emit('atlas:memory-updated', {});
    });

    // Groups, Charters, Treasuries & Forks (P9): the organizational layer. The
    // client only mirrors these; the server owns the Treasury balance (a P3
    // ledger mirror), spend approval (the vote tally), the embezzlement penalty,
    // and a fork's proportional split. Most rows UPDATE in place — a group's
    // `treasurySov`/`state` mutate, a spend's `approved`/`executed` flip, ballots
    // upsert — so insert and update share one handler. A resolving fork DELETEs
    // the leaving `group_member` rows, so that table drops them from the cache.
    conn.db.group.onInsert((_ctx, row) => this.onGroup(row));
    conn.db.group.onUpdate((_ctx, _old, row) => this.onGroup(row));
    conn.db.group.onDelete((_ctx, row) => {
      atlas.groups.delete(row.groupId.toString());
      this.pingTopic('atlas:groups-updated');
    });
    conn.db.groupMember.onInsert((_ctx, row) => this.onGroupMember(row));
    conn.db.groupMember.onUpdate((_ctx, _old, row) => this.onGroupMember(row));
    conn.db.groupMember.onDelete((_ctx, row) => {
      atlas.groupMembers.delete(row.memberId.toString());
      this.pingTopic('atlas:groups-updated');
    });
    conn.db.groupSignature.onInsert((_ctx, row) => this.onGroupSignature(row));
    conn.db.groupSignature.onUpdate((_ctx, _old, row) => this.onGroupSignature(row));
    conn.db.groupSignature.onDelete((_ctx, row) => {
      atlas.groupSignatures.delete(row.signatureId.toString());
      this.pingTopic('atlas:groups-updated');
    });
    conn.db.charterChallenge.onInsert((_ctx, row) => this.onCharterChallenge(row));
    conn.db.charterChallenge.onUpdate((_ctx, _old, row) => this.onCharterChallenge(row));
    conn.db.charterChallenge.onDelete((_ctx, row) => {
      atlas.charterChallenges.delete(row.challengeId.toString());
      this.pingTopic('atlas:groups-updated');
    });
    conn.db.treasurySpend.onInsert((_ctx, row) => this.onTreasurySpend(row));
    conn.db.treasurySpend.onUpdate((_ctx, _old, row) => this.onTreasurySpend(row));
    conn.db.treasurySpend.onDelete((_ctx, row) => {
      atlas.treasurySpends.delete(row.spendId.toString());
      this.pingTopic('atlas:groups-updated');
    });
    conn.db.spendVote.onInsert((_ctx, row) => this.onSpendVote(row));
    conn.db.spendVote.onUpdate((_ctx, _old, row) => this.onSpendVote(row));
    conn.db.spendVote.onDelete((_ctx, row) => {
      atlas.spendVotes.delete(row.voteId.toString());
      this.pingTopic('atlas:groups-updated');
    });
    conn.db.forkVote.onInsert((_ctx, row) => this.onForkVote(row));
    conn.db.forkVote.onUpdate((_ctx, _old, row) => this.onForkVote(row));
    conn.db.forkVote.onDelete((_ctx, row) => {
      atlas.forkVotes.delete(row.forkId.toString());
      this.pingTopic('atlas:groups-updated');
    });
    conn.db.forkBallot.onInsert((_ctx, row) => this.onForkBallot(row));
    conn.db.forkBallot.onUpdate((_ctx, _old, row) => this.onForkBallot(row));
    conn.db.forkBallot.onDelete((_ctx, row) => {
      atlas.forkBallots.delete(row.ballotId.toString());
      this.pingTopic('atlas:groups-updated');
    });
  }

  /** Route a `pulse` row: only the local player's gauges drive the HUD. */
  private onPulse(row: PulseRowLike): void {
    if (atlas.identityHex && row.identity.toHexString() === atlas.identityHex) {
      atlas.localPulse = pulseRowToShared(row);
      bus.emit('atlas:pulse-updated', {});
    }
  }

  /** Route a `compass` row into the visible-Compass cache. */
  private onCompass(row: CompassRowLike): void {
    atlas.compasses.set(row.identity.toHexString(), compassRowToShared(row));
    bus.emit('atlas:compass-updated', {});
  }

  /** Route a `need_satisfier` row into the venue cache (drives the HUD hint). */
  private onNeedSatisfier(row: NeedSatisfierRowLike): void {
    atlas.needSatisfiers.set(row.worldId.toString(), needSatisfierRowToShared(row));
    bus.emit('atlas:pulse-updated', {});
  }

  /** Route an `orbit` row into the Orbit graph. */
  private onOrbit(row: OrbitRowLike): void {
    atlas.orbits.set(row.orbitId.toString(), orbitRowToShared(row));
    bus.emit('atlas:orbit-updated', {});
  }

  /** Route an `orbit_member` row into the membership cache. */
  private onOrbitMember(row: OrbitMemberRowLike): void {
    atlas.orbitMembers.set(row.memberId.toString(), orbitMemberRowToShared(row));
    bus.emit('atlas:orbit-updated', {});
  }

  /** A Wave landed — flash a greeting if the local player is the target. */
  private onWave(row: WaveRowLike): void {
    if (!atlas.identityHex) return;
    if (row.toIdentity.toHexString() !== atlas.identityHex) return;
    const fromHex = row.fromIdentity.toHexString();
    const short = fromHex.length > 12 ? `${fromHex.slice(0, 6)}\u2026` : fromHex;
    const mutual = row.boosted ? ' \u2014 you both connected (+Social)' : '';
    bus.emit('atlas:status', { text: `\ud83d\udc4b ${short} waved at you${mutual}` });
  }

  /** Route a `reputation` row: cache it, and mirror the local player's scores. */
  private onReputation(row: ReputationRowLike): void {
    const scores = reputationRowToShared(row);
    atlas.reputations.set(scores.identity, scores);
    if (atlas.identityHex && scores.identity === atlas.identityHex) {
      atlas.localReputation = scores;
    }
    this.pingTopic('atlas:reputation-updated');
  }

  /** Route a `vouch` row into the trust-graph cache. */
  private onVouch(row: VouchRowLike): void {
    atlas.vouches.set(row.vouchId.toString(), vouchRowToShared(row));
    this.pingTopic('atlas:reputation-updated');
  }

  /** Route a `review` row into the review cache (star + Caution alike). */
  private onReview(row: ReviewRowLike): void {
    atlas.reviews.set(row.reviewId.toString(), reviewRowToShared(row));
    this.pingTopic('atlas:reputation-updated');
  }

  /** Route a `betrayal_record` row into the betrayal cache. */
  private onBetrayal(row: BetrayalRecordRowLike): void {
    atlas.betrayals.set(row.recordId.toString(), betrayalRowToShared(row));
    this.pingTopic('atlas:reputation-updated');
  }

  /** Route an `event` row into the event cache (P8). */
  private onEvent(row: EventRowLike): void {
    atlas.events.set(row.eventId.toString(), eventRowToShared(row));
    this.pingTopic('atlas:events-updated');
  }

  /** Route an `event_attendance` row into the attendance cache (P8). */
  private onAttendance(row: EventAttendanceRowLike): void {
    atlas.attendances.set(row.attendanceId.toString(), attendanceRowToShared(row));
    this.pingTopic('atlas:events-updated');
  }

  /** Route a `ritual` row into the ritual cache (P8). */
  private onRitual(row: RitualRowLike): void {
    atlas.rituals.set(row.ritualId.toString(), ritualRowToShared(row));
    this.pingTopic('atlas:events-updated');
  }

  /** Route a `ritual_mute` row into the invite-mute cache (P8). */
  private onRitualMute(row: RitualMuteRowLike): void {
    atlas.ritualMutes.set(row.muteId.toString(), ritualMuteRowToShared(row));
    this.pingTopic('atlas:events-updated');
  }

  /** Route an `npc_host` row into the host cache (deterministic regular-memory, P8). */
  private onNpcHost(row: NpcHostRowLike): void {
    atlas.npcHosts.set(row.npcId.toString(), npcHostRowToShared(row));
    this.pingTopic('atlas:events-updated');
  }

  /** Route a `memory_entry` row into the Memory Book cache (P8). */
  private onMemoryEntry(row: MemoryEntryRowLike): void {
    atlas.memoryEntries.set(row.entryId.toString(), memoryEntryRowToShared(row));
    bus.emit('atlas:memory-updated', {});
  }

  /** Route a `group` row into the group cache (P9). */
  private onGroup(row: GroupRowLike): void {
    atlas.groups.set(row.groupId.toString(), groupRowToShared(row));
    this.pingTopic('atlas:groups-updated');
  }

  /** Route a `group_member` row into the membership cache (P9). */
  private onGroupMember(row: GroupMemberRowLike): void {
    atlas.groupMembers.set(row.memberId.toString(), groupMemberRowToShared(row));
    this.pingTopic('atlas:groups-updated');
  }

  /** Route a `group_signature` row into the signature cache (P9). */
  private onGroupSignature(row: GroupSignatureRowLike): void {
    atlas.groupSignatures.set(row.signatureId.toString(), groupSignatureRowToShared(row));
    this.pingTopic('atlas:groups-updated');
  }

  /** Route a `charter_challenge` row into the challenge cache (P9). */
  private onCharterChallenge(row: CharterChallengeRowLike): void {
    atlas.charterChallenges.set(row.challengeId.toString(), charterChallengeRowToShared(row));
    this.pingTopic('atlas:groups-updated');
  }

  /** Route a `treasury_spend` row into the spend cache (P9). */
  private onTreasurySpend(row: TreasurySpendRowLike): void {
    atlas.treasurySpends.set(row.spendId.toString(), treasurySpendRowToShared(row));
    this.pingTopic('atlas:groups-updated');
  }

  /** Route a `spend_vote` row into the spend-ballot cache (P9). */
  private onSpendVote(row: SpendVoteRowLike): void {
    atlas.spendVotes.set(row.voteId.toString(), spendVoteRowToShared(row));
    this.pingTopic('atlas:groups-updated');
  }

  /** Route a `fork_vote` row into the fork cache (P9). */
  private onForkVote(row: ForkVoteRowLike): void {
    atlas.forkVotes.set(row.forkId.toString(), forkVoteRowToShared(row));
    this.pingTopic('atlas:groups-updated');
  }

  /** Route a `fork_ballot` row into the fork-ballot cache (P9). */
  private onForkBallot(row: ForkBallotRowLike): void {
    atlas.forkBallots.set(row.ballotId.toString(), forkBallotRowToShared(row));
    this.pingTopic('atlas:groups-updated');
  }

  /** Parse an `avatar` row's JSON columns into a portable appearance. */
  private parseAppearance(row: AvatarRowLike): AvatarAppearance {
    let materialSlots = {};
    let attachments = {};
    try {
      materialSlots = JSON.parse(row.materialSlots);
    } catch {
      /* keep empty on malformed (server normalizes, so this is belt-and-braces) */
    }
    try {
      attachments = JSON.parse(row.attachments);
    } catch {
      /* keep empty */
    }
    return {
      bodyTemplate: row.bodyTemplate as AvatarAppearance['bodyTemplate'],
      skeletonId: row.skeletonId,
      materialSlots,
      attachments,
    };
  }

  /** Route an `asset` row into the global asset cache, then refresh inventory UI. */
  private onAsset(row: AssetRowLike): void {
    atlas.assets.set(row.assetId.toString(), assetRowToShared(row));
    bus.emit('atlas:inventory-updated', {});
  }

  /** Route an `inventory_item` row owned by the local player into state. */
  private onInventoryItem(row: InventoryRowLike): void {
    if (atlas.identityHex && row.owner.toHexString() === atlas.identityHex) {
      atlas.inventory.set(row.itemId.toString(), inventoryRowToShared(row));
    } else {
      // No longer ours (e.g. transferred away) — drop it.
      atlas.inventory.delete(row.itemId.toString());
    }
    bus.emit('atlas:inventory-updated', {});
  }

  /** Route the local player's `wallet` row into state + the HUD. */
  private onWallet(row: WalletRowLike): void {
    if (atlas.identityHex && row.identity.toHexString() === atlas.identityHex) {
      atlas.walletSov = row.sov;
      // Credits unification: INF aliases SOV on the server and wallet.inf is
      // never written — mirroring row.inf showed a permanent 0 and weighed
      // the vouch preview at the minimum.
      atlas.walletInf = row.sov;
      bus.emit('atlas:wallet-updated', { sov: row.sov, inf: row.sov });
    }
  }

  /** Track a `market_listing` row: active listings populate the market, retired ones drop out. */
  private onListing(row: MarketListingRowLike): void {
    const key = row.listingId.toString();
    if (row.active) {
      atlas.listings.set(key, {
        listingId: row.listingId,
        seller: row.seller.toHexString(),
        assetId: row.assetId,
        priceSov: row.priceSov,
        listedAt: row.listedAt.microsSinceUnixEpoch,
      });
    } else {
      atlas.listings.delete(key);
    }
    bus.emit('atlas:listings-updated', {});
  }

  /** An avatar row arrived/updated — route self vs. remote into state + bus. */
  private onAvatar(row: AvatarRowLike): void {
    const hex = row.identity.toHexString();
    const appearance = this.parseAppearance(row);
    if (hex === atlas.identityHex) {
      atlas.localAvatar = { appearance, complexity: row.complexity };
      bus.emit('atlas:avatar-updated', { identityHex: hex, isLocal: true });
    } else {
      const r = atlas.remotes.get(hex);
      if (r) {
        r.appearance = appearance;
        r.complexity = row.complexity;
      }
      bus.emit('atlas:avatar-updated', { identityHex: hex, isLocal: false });
    }
  }

  /** A display-name-change ledger row for self updates the cooldown clock. */
  private onNameChange(row: { identity: { toHexString(): string }; lastChangedAt: { microsSinceUnixEpoch: bigint } }): void {
    if (atlas.identityHex && row.identity.toHexString() === atlas.identityHex) {
      atlas.displayNameLastChangedMicros = row.lastChangedAt.microsSinceUnixEpoch;
      bus.emit('atlas:displayname-updated', {
        displayName: atlas.displayName ?? '',
        lastChangedMicros: atlas.displayNameLastChangedMicros,
      });
    }
  }

  /** A player_state row arrived/updated. Self drives reconciliation; others are remotes. */
  private onPlayer(conn: DbConnection, row: { identity: { toHexString(): string }; worldId: bigint; posX: number; posY: number; posZ: number; yaw: number; animState: string }): void {
    const hex = row.identity.toHexString();
    if (hex === atlas.identityHex) return; // self handled by the avatar module
    if (atlas.currentWorldId !== null && row.worldId !== atlas.currentWorldId) {
      atlas.remotes.delete(hex);
      return;
    }
    // Presence gate (2026-07-21): player_state rows PERSIST after disconnect —
    // without this, every logged-off player rendered as a full primitive avatar
    // forever (~6 meshes each; three r182 pays per-object UBO per mesh).
    const pres = conn.db.presence.identity.find(row.identity as unknown as Parameters<typeof conn.db.presence.identity.find>[0]);
    if (!pres?.online) {
      atlas.remotes.delete(hex);
      return;
    }
    let r = atlas.remotes.get(hex);
    if (!r) {
      r = {
        identityHex: hex,
        mesh: null,
        appearance: null,
        complexity: 0,
        targetX: row.posX,
        targetZ: row.posZ,
        targetYaw: row.yaw,
        snaps: [{ t: performance.now(), x: row.posX, z: row.posZ, yaw: row.yaw }],
        snapIntervalMs: 50, // EMA of inter-arrival; refined by each update below
        animState: row.animState,
        targetY: row.posY,
      };
      // Resolve the portable appearance from the global avatar cache via the indexed PK
      // lookup (O(log N)) — NOT a full avatar-table scan on every remote-player insert.
      // The avatar table is a global subscription (one row per account platform-wide), so
      // the old `for (… of avatar.iter())` was O(accounts) per AOI-entry event → O(N²) on
      // a busy world join. avatar.identity is the btree PK; row.identity is the Identity.
      // row.identity IS an Identity at runtime; the local stub under-types the row field as
      // just `{ toHexString() }`, so cast to the index find's parameter type.
      const av = conn.db.avatar.identity.find(
        row.identity as unknown as Parameters<typeof conn.db.avatar.identity.find>[0],
      );
      if (av) {
        r.appearance = this.parseAppearance(av);
        r.complexity = av.complexity;
      }
      atlas.remotes.set(hex, r);
      bus.emit('atlas:avatar-updated', { identityHex: hex, isLocal: false });
    }
    r.targetX = row.posX;
    r.targetZ = row.posZ;
    r.targetYaw = row.yaw;
    r.animState = row.animState;
    r.targetY = row.posY;
    // Snapshot ring for buffered interpolation (the Valve model: render remotes
    // slightly in the past, sized to the OBSERVED update interval — smooth motion
    // that survives jitter/coalescing instead of exponential-chase rubber-banding).
    const now = performance.now();
    const last = r.snaps[r.snaps.length - 1];
    if (last) {
      const dt = now - last.t;
      // EMA of inter-arrival; ignore sub-ms bursts (same-transaction multi-row commits).
      if (dt > 1) r.snapIntervalMs = r.snapIntervalMs * 0.8 + dt * 0.2;
    }
    r.snaps.push({ t: now, x: row.posX, z: row.posZ, yaw: row.yaw });
    if (r.snaps.length > 24) r.snaps.splice(0, r.snaps.length - 24);
    void conn;
  }

  // ─── World resolution + entry ───────────────────────────────────────────────

  private resolveWorlds(conn: DbConnection): void {
    // Both ids are stable for the session — skip the full world scan once they're resolved
    // (resolveWorlds runs on every enterCommons/toggleWorld, not just the first).
    if (atlas.commonsWorldId !== null && atlas.personalWorldId !== null) return;
    for (const w of conn.db.world.iter()) {
      if (w.kind === 'commons') atlas.commonsWorldId = w.worldId;
      if (w.kind === 'personal' && conn.identity && w.owner.isEqual(conn.identity)) {
        atlas.personalWorldId = w.worldId;
      }
    }
  }

  private enterCommons(): void {
    const conn = atlas.conn;
    if (!conn) return;
    this.resolveWorlds(conn);
    const target = atlas.commonsWorldId ?? 0n;
    void this.enterWorld(target);
  }

  /** Enter a world: clear world-scoped meshes, call the reducer, reset AOI. */
  private async enterWorld(worldId: bigint): Promise<void> {
    const conn = atlas.conn;
    if (!conn) return;
    // Resolve the commons/personal ids on EVERY entry path. They were only
    // resolved inside enterCommons(), so an explicit world pick (the Traveler
    // flow — including picking the Commons card itself) left commonsWorldId
    // null for the whole session and every `currentWorldId === commonsWorldId`
    // gate failed: no terrain streamer, no sea, no settlements.
    this.resolveWorlds(conn);

    // A mid-game SWAP (boot loader already done → loadingGate false): raise the
    // transition loader FIRST so the old area despawns and the new world streams
    // behind a cover — prevents the space→ground teardown overlap flood.
    const midGameSwap = atlas.currentWorldId !== null && !atlas.loadingGate;
    if (midGameSwap) {
      const resolved = worldId === 0n ? (atlas.commonsWorldId ?? worldId) : worldId;
      bus.emit('atlas:world-swapping', { worldId: resolved, fromSpace: atlas.spaceFlight });
    }

    bus.emit('atlas:world-cleared', {});
    atlas.remotes.clear();
    atlas.worldObjects.clear();

    // Reset local transform to the PREDICTED spawn; the server confirms the
    // authoritative pos. The Commons spawns on the hub arrival pad (spawn-stuck
    // law) — the AOI window below centers on this prediction, and a window laid
    // around (0,0) leaves the self row unmirrored (boot then hangs on the
    // self-position gate).
    let spawnX = 0, spawnZ = 0;
    if (worldId === 0n || (atlas.commonsWorldId !== null && worldId === atlas.commonsWorldId)) {
      let seed = '';
      for (const w of conn.db.world.iter()) if (w.kind === 'commons') { seed = w.terrainSeed; break; }
      if (seed !== '') { const sp = commonsSpawnPoint(seed); spawnX = sp.x; spawnZ = sp.z; }
    }
    atlas.localX = spawnX;
    atlas.localY = 1;
    atlas.localZ = spawnZ;

    // Identity-scoped SELF-ROW subscription (one-time): the authoritative row
    // then replicates at ANY coordinate. Cell-window-only coverage of self was
    // the boot-hang trap class — a spawn law change (pad, resume) that lands
    // outside the predicted window left the self row unmirrored forever.
    if (!this.selfRowSubbed && conn.identity) {
      // :sender binds the connection identity server-side (proven to track a
      // 500 m debug_teleport in the match harness); the apply/error result is
      // surfaced for probes — a silently dead self-sub deadlocks far
      // teleports (row leaves the AOI window and adoption never fires).
      (window as unknown as Record<string, unknown>).__selfSub = 'armed';
      conn.subscriptionBuilder()
        .onApplied(() => { (window as unknown as Record<string, unknown>).__selfSub = 'applied'; })
        .onError((ectx) => { (window as unknown as Record<string, unknown>).__selfSub = `error:${String((ectx as { event?: { message?: string } }).event?.message ?? 'subscription error').slice(0, 80)}`; })
        .subscribe(['SELECT * FROM player_state WHERE identity = :sender']);
      this.selfRowSubbed = true;
    }

    // Re-entrancy: two hops inside the ~3 s resume wait used to race — the
    // FIRST hop's continuation resumed last and stamped currentWorldId/AOI for
    // a world the server had already left. Every await below re-checks this.
    const entryGen = ++this.entryGen;
    // Hold AOI recentering while the hop is in flight: localX/Z already name
    // the NEW world's spawn but currentWorldId still names the OLD world, so
    // the next FixedSim tick's maintainAoi saw a cell jump and re-laid the
    // old world's 50-query window (+ versona box) at the new spawn — a wasted
    // round trip per hop whose rows refilled the just-cleared remotes.
    this.hopInFlightGen = entryGen;
    try {
      await conn.reducers.enterWorld({ worldId });
    } catch (err) {
      if (this.hopInFlightGen === entryGen) this.hopInFlightGen = 0; // release: nothing newer took over
      bus.emit('atlas:status', { text: `enterWorld failed: ${(err as Error).message}` });
      return;
    }
    if (entryGen !== this.entryGen) return; // superseded by a newer hop

    // Login-resume: the server may have RESUMED this identity at its logout
    // position instead of the pad. Adopt the authoritative row (it arrives on
    // the self-row subscription regardless of cells) BEFORE laying the AOI
    // window, so the initial window centers on where the player actually is.
    // Bounded wait; on timeout the pad prediction stands (first-entry flow).
    const resolvedId = worldId === 0n ? atlas.commonsWorldId : worldId;
    for (let i = 0; i < 15; i++) {
      const row = conn.identity ? conn.db.playerState.identity.find(conn.identity) : undefined;
      if (row && row.worldId === resolvedId && row.inputSeq === 0) {
        atlas.localX = row.posX;
        atlas.localY = row.posY;
        atlas.localZ = row.posZ;
        break;
      }
      await new Promise((r) => setTimeout(r, 200));
      if (entryGen !== this.entryGen) return; // superseded mid-wait
    }

    // Assign currentWorldId only after the reducer succeeds — if it throws we
    // leave the field unchanged so AOI does not subscribe for a world the player
    // never actually entered (client must mirror authoritative state).
    atlas.currentWorldId = worldId === 0n ? atlas.commonsWorldId : worldId;

    // Lay down the initial AOI window around the spawn cell.
    this.aoiCenterX = worldToCell(atlas.localX);
    this.aoiCenterZ = worldToCell(atlas.localZ);
    this.subscribeAoi(conn, this.aoiCenterX, this.aoiCenterZ);
    this.hopInFlightGen = 0; // the window is laid for the new world — recentering may resume

    bus.emit('atlas:world-entered', { worldId: atlas.currentWorldId ?? worldId });
  }

  /** Follow a SERVER-side world hop (enterSpace/exitSpace already moved our
   *  playerState): the enterWorld() client path minus the reducer call, with
   *  the authoritative arrival position instead of the spawn reset. */
  private adoptWorld(worldId: bigint, x: number, z: number): void {
    const conn = atlas.conn;
    if (!conn) return;
    this.resolveWorlds(conn);
    bus.emit('atlas:world-cleared', {});
    atlas.remotes.clear();
    atlas.worldObjects.clear();
    atlas.localX = x;
    atlas.localY = 1;
    atlas.localZ = z;
    atlas.currentWorldId = worldId;
    this.aoiCenterX = worldToCell(x);
    this.aoiCenterZ = worldToCell(z);
    this.subscribeAoi(conn, this.aoiCenterX, this.aoiCenterZ);
    bus.emit('atlas:world-entered', { worldId });
  }

  private toggleWorld(): void {
    if (atlas.currentWorldId === null) return;
    const inPersonal =
      atlas.personalWorldId !== null && atlas.currentWorldId === atlas.personalWorldId;
    const next = inPersonal ? atlas.commonsWorldId : atlas.personalWorldId;
    if (next !== null && next !== undefined) void this.enterWorld(next);
  }

  // ─── AOI subscription management (with hysteresis) ──────────────────────────

  /** Connection-loss state (reload-and-resume). */
  private lost = false;
  private disposing = false;
  private resumeMarkedAt = 0;

  /**
   * An unclean drop of the SpacetimeDB socket while in a world: remember the
   * world + position, wait for the server to answer again (exponential
   * backoff), then reload — the boot resumes straight into the world. A
   * deliberate disconnect (dispose) and a drop at the menu just show status.
   */
  private onConnectionLost(): void {
    if (this.disposing || this.lost) return;
    if (atlas.currentWorldId == null) { bus.emit('atlas:status', { text: 'disconnected' }); return; }
    this.lost = true;
    markResume({ worldId: atlas.currentWorldId, x: atlas.localX, z: atlas.localZ });
    bus.emit('atlas:status', { text: 'connection lost — reconnecting…' });
    void this.reloadWhenServerBack();
  }

  private async reloadWhenServerBack(): Promise<void> {
    const t0 = Date.now();
    const url = serverPingUrl(STDB_WS_URI);
    for (let attempt = 1; Date.now() - t0 < RECONNECT_GIVE_UP_MS; attempt++) {
      await new Promise((r) => setTimeout(r, reconnectDelayMs(attempt)));
      if (this.disposing) return;
      try {
        const res = await fetch(url, { cache: 'no-store' });
        if (res.ok || res.status === 404) { // any answer = the server is reachable again
          markResume({ worldId: atlas.currentWorldId ?? 0n, x: atlas.localX, z: atlas.localZ });
          bus.emit('atlas:status', { text: 'reconnecting…' });
          location.reload();
          return;
        }
      } catch { /* still unreachable */ }
      bus.emit('atlas:status', { text: `connection lost — retry ${attempt}` });
    }
    bus.emit('atlas:status', { text: 'connection lost — reload to reconnect' });
  }

  private maintainAoi(): void {
    const conn = atlas.conn;
    if (!conn || atlas.currentWorldId === null) return;
    // Keep the resume record fresh (~5 s) so ANY reload within two minutes —
    // the dev server's own, or a network-loss reload — lands back in-world.
    const nowMs = Date.now();
    if (nowMs - this.resumeMarkedAt > 5000) { this.resumeMarkedAt = nowMs; markResume({ worldId: atlas.currentWorldId, x: atlas.localX, z: atlas.localZ }); }
    // Zombie socket (tab frozen / machine asleep): the browser may never
    // deliver onclose — the SDK flags the readyState instead.
    if ((conn as unknown as { isSocketClosed?: boolean }).isSocketClosed && !this.lost) { this.onConnectionLost(); return; }
    if (this.hopInFlightGen !== 0) return; // a hop is in flight: enterWorld lays the new world's window itself
    const cx = worldToCell(atlas.localX);
    const cz = worldToCell(atlas.localZ);
    if (cellManhattan(cx, cz, this.aoiCenterX, this.aoiCenterZ) > AOI_HYSTERESIS_CELLS) {
      this.subscribeAoi(conn, cx, cz);
      this.aoiCenterX = cx;
      this.aoiCenterZ = cz;
    }
  }

  /** Subscribe-new-then-unsubscribe-old so the row cache never has a gap. */
  private subscribeAoi(conn: DbConnection, cx: number, cz: number): void {
    const worldId = atlas.currentWorldId;
    if (worldId === null) return;
    const range = cellRange(cx, cz);
    const prev = this.aoiSub;
    this.aoiSub = conn
      .subscriptionBuilder()
      .onApplied(() => {
        if (prev) prev.unsubscribe();
      })
      // Per-cell equality queries (indexed + server-deduplicated): overlapping cells
      // between the old and new windows are byte-identical strings and re-subscribe
      // for free; only the leading/trailing edge cells cost anything on a shift.
      .subscribe([...nearbyPlayersSql(worldId, range), ...worldObjectsSql(worldId, range)]);
    // versona_state in its OWN handle, BOX-windowed by position (town-life wave:
    // whole-world hot replication at 2 k residents starved the client). The box
    // follows the AOI recenter via subscribe-new-then-unsubscribe-old, so the
    // row cache never gaps; hot-row presence == in-window is the render gate.
    // Separate handle so an older server can reject it without touching the
    // player/object window; the legacy fallback then renders from static rows.
    const prevVersona = this.versonaAoiSub;
    try {
      this.versonaAoiSub = conn
        .subscriptionBuilder()
        .onApplied(() => {
          if (prevVersona) prevVersona.unsubscribe();
        })
        .subscribe([...versonaStateSql(worldId, cx, cz), ...versonaRouteSql(worldId, cx, cz)]); // + the route ahead of each mover (2026-09-15)
    } catch { /* older server — NPCs fall back to legacy render */ }
  }

  // ─── Placement ──────────────────────────────────────────────────────────────

  private placeObject(): void {
    const conn = atlas.conn;
    if (!conn || atlas.currentWorldId === null) return;
    // Owner-only on the server: this no-ops in worlds the player doesn't own.
    if (atlas.personalWorldId === null || atlas.currentWorldId !== atlas.personalWorldId) {
      bus.emit('atlas:status', { text: 'You can only place objects in your own world.' });
      return;
    }
    // Drop a cube one meter in front of the avatar.
    const fx = atlas.localX - Math.sin(atlas.localYaw) * 1.5;
    const fz = atlas.localZ - Math.cos(atlas.localYaw) * 1.5;
    void conn.reducers
      .placeObject({
        worldId: atlas.currentWorldId,
        prefabId: 'welcome_cube',
        posX: fx,
        posY: 0.5,
        posZ: fz,
        rotX: 0,
        rotY: 0,
        rotZ: 0,
        rotW: 1,
        scale: 1,
      })
      .catch((err: Error) => bus.emit('atlas:status', { text: `place failed: ${err.message}` }));
  }

  dispose(): void {
    this.disposing = true; // a deliberate disconnect never reloads
    // Tear down the four bus listeners registered in init() before disconnecting.
    for (const unsub of this.busUnsubs) unsub();
    this.busUnsubs = [];
    this.versonaAoiSub?.unsubscribe();
    this.aoiSub?.unsubscribe();
    this.baseSub?.unsubscribe();
    atlas.conn?.disconnect();
  }
}
